/**
 * DataQualityAlerts Component
 *
 * Displays data quality alerts with severity indicators
 * and recommendations for improvement.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Error as CriticalIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import {
  DataQualityAlert,
  DataQualityResponse,
  AlertSeverity,
  getSeverityColor
} from '../../services/insightService';

interface DataQualityAlertsProps {
  data: DataQualityResponse;
}

const getSeverityIcon = (severity: AlertSeverity) => {
  switch (severity) {
    case 'critical':
      return <CriticalIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'info':
      return <InfoIcon />;
    default:
      return <InfoIcon />;
  }
};

const getQualityLabel = (score: number): string => {
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 50) return 'Fair';
  return 'Needs Improvement';
};

const getQualityColor = (score: number): string => {
  if (score >= 90) return 'success';
  if (score >= 75) return 'info';
  if (score >= 50) return 'warning';
  return 'error';
};

const DataQualityAlerts: React.FC<DataQualityAlertsProps> = ({ data }) => {
  const theme = useTheme();

  return (
    <Box>
      {/* Overall Quality Score */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Data Quality Score</Typography>
            <Chip
              label={getQualityLabel(data.overall_quality_score)}
              color={getQualityColor(data.overall_quality_score) as any}
              size="small"
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={data.overall_quality_score}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                    bgcolor:
                      data.overall_quality_score >= 75
                        ? theme.palette.success.main
                        : data.overall_quality_score >= 50
                        ? theme.palette.warning.main
                        : theme.palette.error.main
                  }
                }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                minWidth: 60,
                color:
                  data.overall_quality_score >= 75
                    ? theme.palette.success.main
                    : data.overall_quality_score >= 50
                    ? theme.palette.warning.main
                    : theme.palette.error.main
              }}
            >
              {data.overall_quality_score.toFixed(0)}%
            </Typography>
          </Box>

          {/* Alert Summary */}
          <Stack direction="row" spacing={2} mt={3}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: data.critical_count > 0
                  ? alpha(theme.palette.error.main, 0.1)
                  : theme.palette.grey[100]
              }}
            >
              <CriticalIcon
                color={data.critical_count > 0 ? 'error' : 'disabled'}
                fontSize="small"
              />
              <Typography variant="body2" fontWeight={600}>
                {data.critical_count} Critical
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: data.warning_count > 0
                  ? alpha(theme.palette.warning.main, 0.1)
                  : theme.palette.grey[100]
              }}
            >
              <WarningIcon
                color={data.warning_count > 0 ? 'warning' : 'disabled'}
                fontSize="small"
              />
              <Typography variant="body2" fontWeight={600}>
                {data.warning_count} Warnings
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: theme.palette.grey[100]
              }}
            >
              <InfoIcon color="info" fontSize="small" />
              <Typography variant="body2" fontWeight={600}>
                {data.info_count} Info
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Alert List */}
      {data.alerts.length === 0 ? (
        <Alert severity="success" icon={<CheckIcon />}>
          <AlertTitle>All Clear</AlertTitle>
          No data quality issues detected. Your emission data is complete and well-documented.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {data.alerts.map((alert) => (
            <Alert
              key={alert.id}
              severity={getSeverityColor(alert.severity)}
              icon={getSeverityIcon(alert.severity)}
              sx={{ alignItems: 'flex-start' }}
            >
              <AlertTitle sx={{ fontWeight: 600 }}>{alert.title}</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {alert.description}
              </Typography>

              <Divider sx={{ my: 1 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Affected: {alert.affected_entity}
                </Typography>
                <Chip
                  label={alert.alert_type.replace('_', ' ')}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', textTransform: 'capitalize' }}
                />
              </Box>

              {alert.recommendation && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Recommended Action:
                  </Typography>
                  <Typography variant="body2">{alert.recommendation}</Typography>
                </Box>
              )}
            </Alert>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default DataQualityAlerts;
